import { useCallback, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import errorLogger, {
  BUGSNAG_STATUSES,
} from '@picsart/growth-rc/services/errorLogger';

import { ErrorMessages } from '@/consts/errors';
import { useAppStore } from '@/store';
import { ToastType } from '@/store/toast/types';
import { AiGenScreenAction } from '@/types/analytics';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import copyToClipboard from '@/utils/file/copyToClipboard';
import isNativeMobile from '@/utils/platform/isNativeMobile';

import openShareSheet from './openShareSheet';
import shareNatively from './shareNatively';
import { fetchShareLink, getCachedShareLink } from './shareLinkQuery';

import type { PresetShareTarget } from './shareTarget';

import type { IAppStore } from '@/store';
import type { ScreenType } from '@/types/analytics';

const selector = (store: IAppStore) => store.showToast;

/**
 * Two actions over one preset link.
 *
 * `sharePreset` opens the export-and-share sheet. `copyPresetLink` copies the
 * link and toasts — a separate control because the sheet can't host a working
 * Copy link for a file-less share, and a toast raised around the sheet's modal
 * is either covered by it or stale by the time it shows.
 *
 * `sharingSlug` / `copyingSlug` name the slug each action has in flight (not
 * bare booleans) so a consumer rendering many presets — a gallery row, the feed
 * — shows the pending state only on the card that was tapped.
 */
const usePresetShare = ({ screenType }: { screenType: ScreenType }) => {
  const showToast = useAppStore(selector);
  const queryClient = useQueryClient();
  const [sharingSlug, setSharingSlug] = useState<string | null>(null);
  const [copyingSlug, setCopyingSlug] = useState<string | null>(null);
  // One guard for both: they hit the same endpoint, and state lags a
  // synchronous second call.
  const isBusyRef = useRef(false);

  const sharePreset = useCallback(
    async (target: PresetShareTarget) => {
      if (isBusyRef.current) return;
      isBusyRef.current = true;
      const { slug } = target;

      sendAiGenScreenActionAnalytics({
        action: AiGenScreenAction.SHARE,
        screen_type: screenType,
        settings: { id: slug },
      });

      try {
        let link = getCachedShareLink({ queryClient, slug }) ?? '';

        if (!link) {
          // Only the spinner is cleared once the link lands; the re-entrancy
          // guard is released in `finally`.
          setSharingSlug(slug);
          try {
            link = await fetchShareLink({ queryClient, target });
          } catch (error) {
            errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN);
            showToast(ErrorMessages.shareLink, ToastType.Error);
            return;
          } finally {
            setSharingSlug(null);
          }
        }

        // Attempted once, and reached with no await at all when the link was
        // cached — every re-share — which is what keeps the tap's transient
        // activation alive for the OS sheet. After a fetch the activation may
        // have lapsed, in which case this reports false and the Picsart sheet
        // takes over.
        if (isNativeMobile() && (await shareNatively(link))) return;

        // Not awaited. `redirectToPackageId` is `resolvify`-based: it neither
        // rejects nor times out, and with `comeback: true` it settles only when
        // the user closes the sheet — so awaiting it would hold the guard for
        // the sheet's lifetime, and forever if the comeback never arrives.
        // Re-entrancy isn't a risk past this point: the sheet covers this
        // miniapp.
        openShareSheet(link).catch((error) => {
          errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN);
          showToast(ErrorMessages.shareSheet, ToastType.Error);
        });
      } finally {
        isBusyRef.current = false;
        setSharingSlug(null);
      }
    },
    [queryClient, screenType, showToast]
  );

  const copyPresetLink = useCallback(
    async (target: PresetShareTarget) => {
      if (isBusyRef.current) return;
      isBusyRef.current = true;
      setCopyingSlug(target.slug);

      sendAiGenScreenActionAnalytics({
        action: AiGenScreenAction.COPY_LINK,
        screen_type: screenType,
        settings: { id: target.slug },
      });

      try {
        // Started, not awaited: Safari revokes the tap's user activation across
        // an await, and the clipboard write needs it. `copyToClipboard` takes
        // the pending value and hands it to `ClipboardItem`, which keeps the
        // activation alive. Rejections are swallowed here so the write gets an
        // empty string and reports the failure below.
        const pending = fetchShareLink({ queryClient, target });
        const copied = await copyToClipboard(pending.catch(() => ''));

        let link = '';
        try {
          link = await pending;
        } catch (error) {
          errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN);
        }

        if (!link) {
          showToast(ErrorMessages.shareLink, ToastType.Error);
          return;
        }
        showToast(
          copied ? 'Link copied to clipboard' : ErrorMessages.copyLink,
          copied ? ToastType.Success : ToastType.Error
        );
      } finally {
        isBusyRef.current = false;
        setCopyingSlug(null);
      }
    },
    [queryClient, screenType, showToast]
  );

  return { sharePreset, copyPresetLink, sharingSlug, copyingSlug };
};

export default usePresetShare;
